import * as React from 'react'
import { IPromise } from 'angular'
import { reactular } from '@vendhq/reactular'
import { Box } from '@lightspeed/design-system-react'
import { ApiCustomer } from '@vend/types'
import { CustomerInfo, PartialCustomerGroup } from '../common/types'
import { contactUpdated } from '../common/customer-contact-utils'
import { useVdFeatureManager } from '../../../../ng-compat/hooks'
import {
  ALL_CUSTOMERS_GROUP_ID,
  detectCustomerFieldFromQuery,
} from '../../../../functions/customer'
import { customerGroupsStore } from '../../../../factories/database/customer-groups-store'
import { $q } from '../../../../ng-compat/imports'
import {
  selectSaleCustomer,
  selectSaleNote,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { useAppSelector } from '../../../../state/hooks'
import { Step1, Step2 } from './Steps'
import { Step1Add } from './Steps/Step1/Step1Add'
import { STEPS } from './Steps/types'

interface Props {
  close: (val?: IPromise<any>) => void
}

export const QuoteCustomerModal: React.FC<Props> = ({ close }) => {
  const [currentStep, setCurrentStep] = React.useState<number>(1)
  const [
    selectedCustomer,
    setSelectedCustomer,
  ] = React.useState<ApiCustomer | null>(null)
  const [newCustomer, setNewCustomer] = React.useState<CustomerInfo | null>(
    null
  )
  const [group, setGroup] = React.useState<PartialCustomerGroup | undefined>(
    undefined
  )
  const [
    originalCustomer,
    setOriginalCustomer,
  ] = React.useState<ApiCustomer | null>(null)

  const saleNote = useAppSelector(selectSaleNote)
  const customer = useAppSelector(selectSaleCustomer)
  const [note, setNote] = React.useState<string>(saleNote)

  const vdFeatureManager = useVdFeatureManager()
  const shouldShowGroup = vdFeatureManager.get('customer_groups')

  if (
    shouldShowGroup &&
    selectedCustomer &&
    selectedCustomer.customer_group_id &&
    !group
  ) {
    customerGroupsStore
      .get(selectedCustomer.customer_group_id)
      .then(groupEntity => {
        if (groupEntity) {
          setGroup({
            id: groupEntity.id,
            name: groupEntity.name,
          })
        }
      })
  }

  if (newCustomer && !group) {
    customerGroupsStore.all().then(groups => {
      if (groups) {
        const defaultGroup = groups.find(
          item => item.group_id === ALL_CUSTOMERS_GROUP_ID
        )
        defaultGroup &&
          setGroup({
            id: defaultGroup.id,
            name: defaultGroup.name,
          })
      }
    })
  }

  const closeAngularModal = () => {
    close($q.resolve())
  }

  const handleStepChange = (step: STEPS) => {
    setCurrentStep(step)
  }

  const handleAddCustomer = (newName?: string) => {
    if (newName) {
      const customer = detectCustomerFieldFromQuery(newName)
      setNewCustomer(prevState => ({
        ...(prevState as CustomerInfo),
        ...customer,
      }))
      setSelectedCustomer(null)
      setOriginalCustomer(null)
      handleStepChange(STEPS.ADD_NEW_CUSTOMER)
    }
  }

  const handleSelectCustomer = (customer: ApiCustomer | null) => {
    setGroup(undefined)
    if (customer) {
      setSelectedCustomer(customer)
      setOriginalCustomer(customer)
      setNewCustomer(null)
    } else {
      setSelectedCustomer(null)
    }
  }

  const handleSelectedContactChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault()
    const { value, name } = e.target
    if (name) {
      setSelectedCustomer((prev: $TSFixMe) => {
        return {
          ...prev,
          [name]: value,
        }
      })
    }
  }

  const handleNewContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    const { value, name } = e.target
    if (name) {
      setNewCustomer(prevState => ({
        ...(prevState as CustomerInfo),
        [name]: value,
      }))
    }
  }

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target
    if (value) {
      setNote(value)
    } else {
      setNote('')
    }
  }

  React.useEffect(() => {
    if (customer && customer.customer_code !== 'WALKIN') {
      setOriginalCustomer(customer)
      setSelectedCustomer(customer)
    }
  }, [customer, setSelectedCustomer])

  const getStepComponent = () => {
    switch (currentStep) {
      case STEPS.SELECT_CUSTOMER:
        return (
          <Step1
            onClose={closeAngularModal}
            onStepChange={handleStepChange}
            onAdd={handleAddCustomer}
            onSelect={handleSelectCustomer}
            onContactChange={handleSelectedContactChange}
            selectedCustomer={selectedCustomer}
            newCustomer={newCustomer}
            group={group}
          />
        )
      case STEPS.ADD_NEW_CUSTOMER:
        return (
          <Step1Add
            onStepChange={handleStepChange}
            onClose={closeAngularModal}
            onChange={handleNewContactChange}
            newCustomer={newCustomer}
          />
        )
      case STEPS.REVIEW_DETAILS:
        return (
          <Step2
            onClose={closeAngularModal}
            onStepChange={handleStepChange}
            newCustomer={newCustomer}
            selectedCustomer={selectedCustomer}
            group={group}
            contactChanged={contactUpdated(selectedCustomer, originalCustomer)}
            note={note}
            onNoteChange={handleNoteChange}
          />
        )
    }
  }

  return (
    <Box className="quote-customer-modal" data-cy="quote-customer-modal">
      {getStepComponent()}
    </Box>
  )
}

QuoteCustomerModal.displayName = 'QuoteCustomerModal'

export const QuoteCustomerModalComponent = reactular(
  QuoteCustomerModal,
  ['close'],
  'reactWrapper'
)
